import { useState, type FormEvent } from 'react'
import { Hash, Volume2, MessagesSquare } from 'lucide-react'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { describeError } from '@/lib/auth'
import { ChannelType } from '@/types/api'
import { cx } from '@/lib/cx'
import { Modal } from '../settings/Modal'
import { Field } from '@/pages/AuthPage'
import { channelName } from '@/lib/channelName'
import { ChannelNameInput } from './ChannelNameInput'
import auth from '@/pages/AuthPage.module.css'
import styles from './CreateChannelModal.module.css'

interface Props {
  guildId: string
  parentId: string | null
  initialType: number
  onClose: () => void
}

export function CreateChannelModal({ guildId, parentId, initialType, onClose }: Props) {
  const isCategory = initialType === ChannelType.GuildCategory
  const [type, setType] = useState<number>(isCategory ? ChannelType.GuildCategory : initialType)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const createChannel = useGuildStore((s) => s.createChannel)
  const parent = useGuildStore((s) => (parentId ? s.channels[guildId]?.[parentId] : undefined))
  const setView = useAppStore((s) => s.setView)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const ch = await createChannel(guildId, { name: name.trim(), type, ...(parentId && !isCategory ? { parent_id: parentId } : {}) })
      if ((ch.type === ChannelType.GuildText || ch.type === ChannelType.GuildForum)) setView({ kind: 'guild', guildId, channelId: ch.id })
      onClose()
    } catch (err) {
      const d = describeError(err)
      setError(d.fields.name ?? d.message)
      setBusy(false)
    }
  }

  const preview = channelName(name, (type === ChannelType.GuildText || type === ChannelType.GuildForum))

  return (
    <Modal title={isCategory ? 'Создать категорию' : 'Создать канал'} onClose={onClose} width={460}>
      <form className={auth.form} onSubmit={submit} noValidate>
        {parent ? <p className={styles.parent}>в категории <strong>{parent.name}</strong></p> : null}
        {!isCategory ? (
          <div className={styles.types} role="radiogroup" aria-label="Тип канала">
            {[
              { t: ChannelType.GuildText, icon: <Hash size={22} />, label: 'Текстовый', hint: 'Сообщения, картинки, ссылки' },
              { t: ChannelType.GuildForum, icon: <MessagesSquare size={22} />, label: 'Форум', hint: 'Темы и обсуждения' },
              { t: ChannelType.GuildVoice, icon: <Volume2 size={22} />, label: 'Голосовой', hint: 'Голос, видео, демонстрация экрана' },
            ].map((o) => (
              <button key={o.t} type="button" role="radio" aria-checked={type === o.t} className={cx(styles.type, type === o.t && styles.typeActive)} onClick={() => setType(o.t)}>
                <span className={styles.typeIcon}>{o.icon}</span>
                <span className={styles.typeText}>
                  <span className={styles.typeLabel}>{o.label}</span>
                  <span className={styles.typeHint}>{o.hint}</span>
                </span>
              </button>
            ))}
          </div>
        ) : null}
        <Field label={isCategory ? 'Название категории' : 'Название канала'} htmlFor="channel-name" error={error ?? undefined} hint={type === ChannelType.GuildText && preview ? `Будет называться #${preview}` : undefined}>
          <ChannelNameInput id="channel-name" value={name} onChange={setName} placeholder={isCategory ? 'Новая категория' : 'новый-канал'} autoFocus required />
        </Field>
        <button type="submit" className={auth.primary} disabled={busy || !name.trim()}>{busy ? 'Создаём…' : 'Создать'}</button>
      </form>
    </Modal>
  )
}
